import React, { useEffect, useRef, useState } from 'react';
import { format } from 'date-fns';
import Layout from '../components/layout/Layout';
import { useFamilyStore } from '../stores/familyStore';
import { useAuthStore } from '../stores/authStore';
import { cn } from '../utils/cn';
import { useEntitlements } from '../hooks/useEntitlements';
import { convex } from '../lib/convex';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';
import { useUser } from '@clerk/clerk-react';
import type { ActivityLogEntry } from '../types/ActivityLog';
// removed jsPDF generation; we provide print-friendly HTML instead
import { ActivityLogList } from '../components/family/ActivityLogList';
import { MonthYearPicker } from '../components/ui/MonthYearPicker';

type AccountTab = 'plan' | 'security' | 'history' | 'reports';

const Account: React.FC = () => {
  const { user } = useAuthStore();
  const { family, members, isLoading: familyLoading, initialize } = useFamilyStore();
  const { user: clerkUser } = useUser();
  const entitlements = useEntitlements(family?.id);

  const [activeTab, setActiveTab] = useState<AccountTab>(() => {
    const saved = sessionStorage.getItem('account-active-tab');
    return (saved as AccountTab) || 'plan';
  });

  // Password change form state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  // Activity log state
  const [activityLogs, setActivityLogs] = useState<ActivityLogEntry[]>([]);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  const [logsError, setLogsError] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [exportMonth, setExportMonth] = useState<string>(new Date().toISOString().slice(0, 7));
  const [historyPlan, setHistoryPlan] = useState<'basic' | 'pro' | 'max' | 'trial' | null>(null);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyTotal, setHistoryTotal] = useState(0);
  const pageSize = 25;
  const [actionFilter, setActionFilter] = useState<string>('');
  const [reportMonth, setReportMonth] = useState<string>(new Date().toISOString().slice(0, 7));
  const [careReportMonth, setCareReportMonth] = useState<string>(new Date().toISOString().slice(0, 7));

  // Plan data from reactive entitlements
  const planId = entitlements?.planId ?? null;
  const loadingPlan = !entitlements;

  const initRef = useRef(false);

  useEffect(() => {
    if (user && !family && !familyLoading && !initRef.current) {
      initRef.current = true;
      initialize();
    }
  }, [user, family, familyLoading, initialize]);

  useEffect(() => {
    sessionStorage.setItem('account-active-tab', activeTab);
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'history' && family) {
      // Reset page on tab entry or filter change
      setHistoryPage(0);
      void fetchActivityLogs(0);
    }
  }, [activeTab, family, actionFilter]);

  // Persist/restore report/export months
  useEffect(() => {
    try {
      const rm = localStorage.getItem('reportMonth');
      if (rm && /^\d{4}-\d{2}$/.test(rm)) setReportMonth(rm);
    } catch { }
    try {
      const em = localStorage.getItem('exportMonth');
      if (em && /^\d{4}-\d{2}$/.test(em)) setExportMonth(em);
    } catch { }
    try {
      const cm = localStorage.getItem('careReportMonth');
      if (cm && /^\d{4}-\d{2}$/.test(cm)) setCareReportMonth(cm);
    } catch { }
  }, []);

  useEffect(() => {
    try { localStorage.setItem('reportMonth', reportMonth); } catch { }
  }, [reportMonth]);

  useEffect(() => {
    try { localStorage.setItem('exportMonth', exportMonth); } catch { }
  }, [exportMonth]);

  useEffect(() => {
    try { localStorage.setItem('careReportMonth', careReportMonth); } catch { }
  }, [careReportMonth]);

  const fetchActivityLogs = async (page: number) => {
    if (!family || !user) return;
    setIsLoadingLogs(true);
    setLogsError(null);
    try {
      const plan = entitlements?.planId ?? 'basic';
      setHistoryPlan(plan);

      // Fetch activity from Convex
      const maxItems = plan === 'basic' ? 5 : 500;
      const rawActivities = await convex.query(api.activity.queries.getRecentActivity, {
        familyId: family.id as Id<"families">,
        limit: maxItems,
      });

      // Map Convex camelCase to ActivityLogEntry snake_case
      let mapped: ActivityLogEntry[] = (rawActivities || []).map((a: any) => ({
        id: a._id,
        family_id: a.familyId,
        user_id: a.userId,
        user_email: a.userEmail,
        action: a.action,
        details: a.details || '',
        created_at: a._creationTime ? new Date(a._creationTime).toISOString() : '',
        metadata: a.metadata,
        entity_name: a.entityName,
        entity_type: a.entityType,
        entity_id: a.entityId,
        changes: a.changes,
      }));

      // Apply action filter client-side
      if (actionFilter) {
        mapped = mapped.filter(a => a.action === actionFilter);
      }

      // Apply date range filter for Pro/Max
      if (plan !== 'basic') {
        const days = plan === 'pro' ? 30 : 180;
        const fromDate = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
        mapped = mapped.filter(a => a.created_at >= fromDate);
      }

      setHistoryTotal(mapped.length);

      if (plan === 'basic') {
        setActivityLogs(mapped.slice(0, 5));
        setHistoryPage(0);
      } else {
        const from = page * pageSize;
        const to = from + pageSize;
        setActivityLogs(mapped.slice(from, to));
        setHistoryPage(page);
      }
    } catch (err: any) {
      setLogsError(err.message || 'An error occurred while fetching activity history');
    } finally {
      setIsLoadingLogs(false);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError('');
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters long');
      return;
    }
    setIsChangingPassword(true);
    try {
      if (!clerkUser) throw new Error('Not authenticated');
      await clerkUser.updatePassword({
        currentPassword,
        newPassword,
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      alert('Password changed successfully!');
    } catch (error: any) {
      const msg = error?.errors?.[0]?.longMessage || error?.message || 'An error occurred while changing password';
      setPasswordError(msg);
    } finally {
      setIsChangingPassword(false);
    }
  };

  const handleExportData = async () => {
    if (!family || !user) {
      alert('Missing family or user data.');
      return;
    }
    setIsExporting(true);
    try {
      const familyId = family.id as Id<"families">;

      // Export month range
      const monthStr = exportMonth;
      const [y, m] = monthStr.split('-').map(Number);
      const start = new Date(y, m - 1, 1);
      const end = new Date(y, m, 0, 23, 59, 59, 999);
      const startIso = start.toISOString().slice(0, 10);
      const endIso = end.toISOString().slice(0, 10);

      const [familyData, childrenData, membersData, calendarData, eventsData] = await Promise.all([
        convex.query(api.families.queries.getFamily, { familyId }),
        convex.query(api.families.queries.getChildren, { familyId }),
        convex.query(api.families.queries.getFamilyMembers, { familyId }),
        convex.query(api.calendar.queries.getEntriesByFamily, {
          familyId,
          startDate: startIso,
          endDate: endIso,
        }),
        convex.query(api.events.queries.getEvents, {
          familyId,
          startDate: new Date(y, m - 1, 1).toISOString(),
          endDate: end.toISOString(),
        }),
      ]);

      const exportPayload = {
        exported_at: new Date().toISOString(),
        month: monthStr,
        family: familyData || null,
        children: childrenData || [],
        members: membersData || [],
        calendar_entries: calendarData || [],
        events: eventsData || []
      };

      const blob = new Blob([JSON.stringify(exportPayload, null, 2)], { type: 'application/json;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `2homes-export-${familyId}-${monthStr}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export error:', err);
      alert('Failed to export data. Please try again.');
    } finally {
      setIsExporting(false);
    }
  };

  // removed PDF generation function

  const handlePrintExpenseReport = async () => {
    if (!family || !user) {
      alert('Log in and select a family.');
      return;
    }
    const monthStr = reportMonth;
    if (!monthStr || !/^\d{4}-\d{2}$/.test(monthStr)) return;
    const [year, month] = monthStr.split('-').map(Number);
    const start = new Date(year, month - 1, 1);
    const end = new Date(year, month, 0, 23, 59, 59, 999);

    // Open window IMMEDIATELY after click (iOS requires sync with gesture)
    const preview = window.open('', '_blank');
    if (preview) {
      preview.document.open();
      preview.document.write(`<!doctype html><html lang="en"><head><meta charset="utf-8" /><meta name="viewport" content="width=device-width, initial-scale=1" /><title>Generating…</title></head><body style="font-family: system-ui, -apple-system, Segoe UI, Roboto, sans-serif; padding: 16px; color:#0f172a;"><div>Generating expense report — ${monthStr}…</div></body></html>`);
      preview.document.close();
    }

    try {
      const familyId = family.id as Id<"families">;

      // Get members from store (already loaded reactively)
      const parents = members.filter(m => m.role === 'parent_a' || m.role === 'parent_b');
      const parentA = parents.find(p => p.role === 'parent_a');
      const parentB = parents.find(p => p.role === 'parent_b');
      const userIdToEmail = new Map<string, string>();
      members.forEach(m => { if (m.user_id && m.user_email) userIdToEmail.set(m.user_id, m.user_email); });

      const childrenData = await convex.query(api.families.queries.getChildren, { familyId });
      const childIdToName = new Map<string, string>((childrenData || []).map((c: any) => [c._id, c.name]));

      const allEvents = await convex.query(api.events.queries.getEvents, {
        familyId,
        startDate: start.toISOString(),
        endDate: end.toISOString(),
      });

      const expenses = (allEvents || []).filter((e: any) => e.type === 'expense');

      const items = expenses.map((e: any) => ({
        date: new Date(e.startAt).toISOString().slice(0, 10),
        title: e.title || 'Expense',
        amount: typeof e.amount === 'number' ? e.amount : 0,
        currency: e.currency || 'USD',
        childName: childIdToName.get(e.childId) || '—',
        parentEmail: userIdToEmail.get(e.createdBy) || '—',
      }));

      const byParent = new Map<string, typeof items>();
      for (const it of items) {
        const key = it.parentEmail || '—';
        if (!byParent.has(key)) byParent.set(key, []);
        byParent.get(key)!.push(it);
      }

      const title = `Expense Report — ${monthStr}`;
      const stamp = format(new Date(), 'dd MMM yyyy, HH:mm');
      const email = user?.email || '—';

      const styles = `
        <style>
          @page { margin: 16mm; }
          body { font-family: 'Noto Sans', system-ui, -apple-system, Segoe UI, Roboto, sans-serif; color: #0f172a; font-size: 14px; line-height: 1.5; }
          h1 { font-size: 22px; margin: 0 0 10px; }
          .meta { font-size: 14px; margin: 0 0 8px; }
          .section { margin: 18px 0; }
          .section h2 { font-size: 16px; margin: 0 0 10px; }
          table { width: 100%; border-collapse: collapse; font-size: 14px; }
          th, td { border: 1px solid #e2e8f0; padding: 8px 10px; text-align: left; }
          th { background: #e9f0ff; }
          .child { margin-bottom: 16px; }
          .child thead th { background: #f1f5f9; }
          .footer { position: fixed; bottom: 10mm; left: 16mm; right: 16mm; font-size: 11px; color: #475569; }
        </style>
      `;

      let sectionsHtml = '';
      let totalsByCurrency = new Map<string, number>();

      for (const [parent, list] of byParent.entries()) {
        const rows = list.map((it: any) => `
          <tr>
            <td>${it.date}</td>
            <td>${it.childName}</td>
            <td>${it.title}</td>
            <td>${it.amount.toFixed(2)}</td>
            <td>${it.currency}</td>
          </tr>
        `).join('');

        const parentTotalsByCurrency = new Map<string, number>();
        for (const item of list) {
          const currency = item.currency || 'USD';
          const currentTotal = parentTotalsByCurrency.get(currency) || 0;
          parentTotalsByCurrency.set(currency, currentTotal + (typeof item.amount === 'number' ? item.amount : 0));

          const globalTotal = totalsByCurrency.get(currency) || 0;
          totalsByCurrency.set(currency, globalTotal + (typeof item.amount === 'number' ? item.amount : 0));
        }

        let parentTotalsHtml = '';
        for (const [currency, total] of parentTotalsByCurrency.entries()) {
          parentTotalsHtml += `<tr><td colspan="3">Total (${parent}) - ${currency}</td><td>${total.toFixed(2)}</td><td>${currency}</td></tr>`;
        }

        sectionsHtml += `
          <div class="section">
            <h2>Parent: ${parent}</h2>
            ${list.length === 0 ? '<div class="meta">No expenses this month.</div>' : `
            <table>
              <thead>
                <tr><th>Date</th><th>Child</th><th>Title</th><th>Amount</th><th>Currency</th></tr>
              </thead>
              <tbody>${rows}</tbody>
              <tfoot>${parentTotalsHtml}</tfoot>
            </table>`}
          </div>
        `;
      }

      const isMobile = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) || (navigator.maxTouchPoints || 0) > 1;
      const html = `
        <!doctype html>
        <html lang="en">
          <head>
            <meta charset="utf-8" />
            <meta name="viewport" content="width=device-width, initial-scale=1" />
            <link href="https://fonts.googleapis.com/css2?family=Noto+Sans:wght@400;600&display=swap&subset=latin-ext" rel="stylesheet" />
            ${styles}
            <title>${title}</title>
          </head>
          <body>
            <div style="display:flex; align-items:center; justify-content:space-between; gap:12px;">
              <div>
                <h1>${title}</h1>
                <div class="meta"><strong>Parent A</strong>: ${parentA?.user_email || '—'}</div>
                <div class="meta"><strong>Parent B</strong>: ${parentB?.user_email || '—'}</div>
              </div>
              <div style="display:flex; gap:8px; align-items:center;">
                <button id="closeBtn" style="padding:8px 12px; font-size:12px; background:#fff; color:#1e293b; border:1px solid #cbd5e1; border-radius:6px;">Close</button>
                <button id="printBtn" style="display:${isMobile ? 'inline-block' : 'none'}; padding:8px 12px; font-size:12px; background:#1e293b; color:#fff; border:0; border-radius:6px;">Print</button>
              </div>
            </div>
            ${sectionsHtml}
            <div class="section" style="margin-top:24px;">
              <h2>Total Summary by Currency:</h2>
              ${Array.from(totalsByCurrency.entries()).map(([currency, total]) =>
        `<div style="font-size: 13px; font-weight:600; margin: 8px 0;">${currency}: ${total.toFixed(2)}</div>`
      ).join('')}
            </div>
            <div class="footer">Report generated from 2homes.app — account: ${email} — ${stamp}</div>
            <script>
              (function(){
                var isMobile = ${isMobile ? 'true' : 'false'};
                function goPrint(){ try { window.print(); } catch(e){} }
                function goClose(){ try { window.close(); setTimeout(function(){ if (!document.hidden) { window.location.href = '/account'; } }, 50); } catch(e){ try { window.location.href = '/account'; } catch(_){} } }
                if (!isMobile) {
                  window.onload = function(){ setTimeout(goPrint, 300); };
                } else {
                  document.getElementById('printBtn')?.addEventListener('click', goPrint);
                }
                document.getElementById('closeBtn')?.addEventListener('click', goClose);
              })();
            </script>
          </body>
        </html>
      `;

      const w = preview || window.open('', '_blank');
      if (!w) throw new Error('Popup blocked');
      w.document.open();
      w.document.write(html);
      w.document.close();
    } catch (e: any) {
      alert(`Failed to generate report: ${e.message || e}`);
    }
  };

  const handlePrintCareReport = async () => {
    if (!family || !user) {
      alert('Log in and select a family.');
      return;
    }
    const monthStr = careReportMonth;
    if (!monthStr || !/^\d{4}-\d{2}$/.test(monthStr)) return;
    const [year, month] = monthStr.split('-').map(Number);
    const daysInMonth = new Date(year, month, 0).getDate();
    const startIso = `${monthStr}-01`;
    const endIso = `${monthStr}-${String(daysInMonth).padStart(2, '0')}`;

    // Open window IMMEDIATELY after click (iOS requires sync with gesture)
    const preview = window.open('', '_blank');
    if (preview) {
      preview.document.open();
      preview.document.write(`<!doctype html><html lang="en"><head><meta charset="utf-8" /><meta name="viewport" content="width=device-width, initial-scale=1" /><title>Generating…</title></head><body style="font-family: system-ui, -apple-system, Segoe UI, Roboto, sans-serif; padding: 16px; color:#0f172a;"><div>Generating care report — ${monthStr}…</div></body></html>`);
      preview.document.close();
    }

    try {
      const familyId = family.id as Id<"families">;

      // Get members from store (already loaded reactively)
      const parents = members.filter(m => m.role === 'parent_a' || m.role === 'parent_b');
      const parentA = parents.find(p => p.role === 'parent_a');
      const parentB = parents.find(p => p.role === 'parent_b');

      const childrenData = await convex.query(api.families.queries.getChildren, { familyId });
      const children = (childrenData || []) as Array<{ _id: string; name: string }>;
      const childIdToName = new Map<string, string>(children.map(c => [c._id, c.name]));

      const entries = await convex.query(api.calendar.queries.getEntriesByFamily, {
        familyId,
        startDate: startIso,
        endDate: endIso,
      });

      // Count days for each child (deduplication: 1 day = max 1 entry)
      type Totals = { home_a: number; home_b: number; unassigned: number };
      const totalsByChild = new Map<string, Totals>();
      for (const c of children) {
        totalsByChild.set(c._id, { home_a: 0, home_b: 0, unassigned: daysInMonth });
      }
      // Build unique days map per child, choosing latest entry (by _creationTime)
      const latestPerDay = new Map<string, { childId: string; date: string; location: 'home_a' | 'home_b'; _creationTime?: number }>();
      for (const e of (entries || []) as Array<{ _id: string; childId: string; date: string; location: 'home_a' | 'home_b'; _creationTime?: number }>) {
        const key = `${e.childId}::${e.date}`;
        const current = latestPerDay.get(key);
        if (!current) {
          latestPerDay.set(key, e);
        } else {
          const currTs = current._creationTime || 0;
          const newTs = e._creationTime || 0;
          if (newTs >= currTs) latestPerDay.set(key, e);
        }
      }

      for (const e of latestPerDay.values()) {
        const t = totalsByChild.get(e.childId);
        if (!t) continue;
        if (e.location === 'home_a') t.home_a += 1;
        else if (e.location === 'home_b') t.home_b += 1;
        if (t.unassigned > 0) t.unassigned -= 1;
      }

      // Correct unassigned so always: unassigned = days_in_month - A - B
      for (const [childId, t] of totalsByChild.entries()) {
        t.unassigned = Math.max(0, daysInMonth - t.home_a - t.home_b);
        totalsByChild.set(childId, t);
      }

      const title = `Care Report — ${monthStr}`;
      const stamp = format(new Date(), 'dd MMM yyyy, HH:mm');
      const email = user?.email || '—';

      const styles = `
        <style>
          @page { margin: 16mm; }
          body { font-family: 'Noto Sans', system-ui, -apple-system, Segoe UI, Roboto, sans-serif; color: #0f172a; }
          h1 { font-size: 18px; margin: 0 0 8px; }
          .meta { font-size: 12px; margin: 0 0 16px; }
          .section { margin: 16px 0; }
          .section h2 { font-size: 14px; margin: 0 0 8px; }
          table { width: 100%; border-collapse: collapse; font-size: 12px; }
          th, td { border: 1px solid #e2e8f0; padding: 6px 8px; text-align: left; }
          th { background: #e9f0ff; }
          tfoot td { font-weight: 600; }
          .footer { position: fixed; bottom: 10mm; left: 16mm; right: 16mm; font-size: 10px; color: #475569; }
        </style>
      `;

      const toPct = (n: number) => ((n / daysInMonth) * 100).toFixed(1) + '%';
      const childBlocks = Array.from(totalsByChild.entries()).map(([childId, t]) => {
        const name = childIdToName.get(childId) || '—';
        const a = t.home_a;
        const b = t.home_b;
        const u = Math.max(0, daysInMonth - a - b);
        return `
          <table class="child">
            <thead>
              <tr><th colspan="2">${name}</th></tr>
            </thead>
            <tbody>
              <tr>
                <td>Home A (days)</td>
                <td>${a}</td>
              </tr>
              <tr>
                <td>Home A (%)</td>
                <td>${toPct(a)}</td>
              </tr>
              <tr>
                <td>Home B (days)</td>
                <td>${b}</td>
              </tr>
              <tr>
                <td>Home B (%)</td>
                <td>${toPct(b)}</td>
              </tr>
              <tr>
                <td>Unassigned (days)</td>
                <td>${u}</td>
              </tr>
              <tr>
                <td>Unassigned (%)</td>
                <td>${toPct(u)}</td>
              </tr>
            </tbody>
          </table>
        `;
      }).join('');

      const isMobile = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) || (navigator.maxTouchPoints || 0) > 1;
      const html = `
        <!doctype html>
        <html lang="en">
          <head>
            <meta charset="utf-8" />
            <meta name="viewport" content="width=device-width, initial-scale=1" />
            <link href="https://fonts.googleapis.com/css2?family=Noto+Sans:wght@400;600&display=swap&subset=latin-ext" rel="stylesheet" />
            ${styles}
            <title>${title}</title>
          </head>
          <body>
            <div style="display:flex; align-items:flex-start; justify-content:space-between; gap:12px;">
              <div>
                <h1>${title}</h1>
                <div class="meta"><strong>Home A:</strong> ${parentA?.user_email || '—'}</div>
                <div class="meta"><strong>Home B:</strong> ${parentB?.user_email || '—'}</div>
                <div class="meta">Days in month: ${daysInMonth}</div>
              </div>
              <div style="display:flex; gap:8px; align-items:center;">
                <button id="closeBtn" style="padding:8px 12px; font-size:12px; background:#fff; color:#1e293b; border:1px solid #cbd5e1; border-radius:6px;">Close</button>
                <button id="printBtn" style="display:${isMobile ? 'inline-block' : 'none'}; padding:8px 12px; font-size:13px; background:#1e293b; color:#fff; border:0; border-radius:6px;">Print</button>
              </div>
            </div>
            <div class="section">
              ${childBlocks}
            </div>
            <div class="footer">Report generated from 2homes.app — account: ${email} — ${stamp}</div>
            <script>
              (function(){
                var isMobile = ${isMobile ? 'true' : 'false'};
                function goPrint(){ try { window.print(); } catch(e){} }
                function goClose(){ try { window.close(); setTimeout(function(){ if (!document.hidden) { window.location.href = '/account'; } }, 50); } catch(e){ try { window.location.href = '/account'; } catch(_){} } }
                if (!isMobile) {
                  window.onload = function(){ setTimeout(goPrint, 300); };
                } else {
                  document.getElementById('printBtn')?.addEventListener('click', goPrint);
                }
                document.getElementById('closeBtn')?.addEventListener('click', goClose);
              })();
            </script>
          </body>
        </html>
      `;

      const w = preview || window.open('', '_blank');
      if (!w) throw new Error('Popup blocked');
      w.document.open();
      w.document.write(html);
      w.document.close();
    } catch (e: any) {
      alert(`Failed to generate report: ${e.message || e}`);
    }
  };

  return (
    <Layout>
      <div className="bg-gray-50">
        {/* Tabs */}
        <div className="bg-white border-b sticky top-0 z-10">
          <div className="px-4 py-2">
            <div className="grid grid-cols-4 gap-1">
              {[
                { id: 'plan', label: 'Plan', icon: '🏷️' },
                { id: 'security', label: 'Password', icon: '🔒' },
                { id: 'history', label: 'History', icon: '📃' },
                { id: 'reports', label: 'Reports', icon: '📊' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as AccountTab)}
                  className={cn(
                    'flex flex-col items-center justify-center p-2 rounded-lg cursor-pointer select-none',
                    'transition-colors duration-100 active:scale-95',
                    'text-xs font-medium touch-manipulation',
                    activeTab === tab.id
                      ? 'bg-blue-100 text-blue-600 border-2 border-blue-200'
                      : 'text-gray-600 hover:bg-gray-100 border-2 border-transparent'
                  )}
                >
                  <span className="text-lg mb-1">{tab.icon}</span>
                  <span className="leading-tight text-center">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="p-3">
          {/* Plan */}
          {activeTab === 'plan' && (
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="p-4 border-b bg-gray-50">
                <h2 className="text-lg font-semibold text-gray-800 flex items-center">
                  <span className="mr-2">🏷️</span>
                  Your Plan
                </h2>
              </div>
              <div className="p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-slate-700">
                    Status: {loadingPlan ? 'Loading…' : (planId === 'trial' ? '14-Day Free Trial' : (planId ? planId : '—'))}
                  </div>
                  <a href="/pricing" className="btn btn-sm btn-primary w-[7.5rem] inline-flex items-center justify-center text-center">Pricing</a>
                </div>
                <p className="text-xs text-slate-600">Change or refresh plan on Pricing page. If you have an active subscription, changes will appear shortly.</p>
              </div>
            </div>
          )}

          {/* Password */}
          {activeTab === 'security' && (
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="p-4 border-b bg-gray-50">
                <h2 className="text-lg font-semibold text-gray-800 flex items-center">
                  <span className="mr-2">🔒</span>
                  Change Password
                </h2>
                <p className="text-sm text-gray-600 mt-1">Password should be strong and unique</p>
              </div>
              <div className="p-4">
                <form onSubmit={handleChangePassword}>
                  {passwordError && (
                    <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg">{passwordError}</div>
                  )}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Current password</label>
                      <input
                        type="password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        disabled={isChangingPassword}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                        placeholder="Enter current password"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">New password</label>
                      <input
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        disabled={isChangingPassword}
                        minLength={8}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                        placeholder="At least 8 characters"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Confirm new password</label>
                      <input
                        type="password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        disabled={isChangingPassword}
                        minLength={8}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-base"
                        placeholder="Repeat new password"
                        required
                      />
                    </div>
                  </div>
                  <div className="pt-6 border-t mt-6">
                    <button
                      type="submit"
                      disabled={isChangingPassword || !currentPassword || !newPassword || !confirmPassword}
                      className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-6 py-3 rounded-lg font-medium flex items-center justify-center text-base"
                    >
                      {isChangingPassword ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                          Changing password...
                        </>
                      ) : (
                        <>
                          <span className="mr-2">🔒</span>
                          Change password
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* History */}
          {activeTab === 'history' && (
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="p-2 border-b bg-gray-50">
                <h2 className="text-base font-semibold text-gray-800 flex items-center">
                  <span className="mr-2">📃</span>
                  Activity History
                </h2>
              </div>
              <div className="p-2">
                <div className="mb-3">
                  <div className="flex items-center justify-between">
                    <div className="mt-2 flex items-center gap-2">
                      <label className="text-xs text-gray-600">Action filter:</label>
                      <select
                        value={actionFilter}
                        onChange={(e) => setActionFilter(e.target.value)}
                        className="text-xs border rounded px-2 py-1 bg-white"
                      >
                        <option value="">All</option>
                        <option value="calendar_entry_created">Added calendar entry</option>
                        <option value="calendar_entry_updated">Updated calendar entry</option>
                        <option value="calendar_entry_deleted">Deleted calendar entry</option>
                        <option value="note_created">Added note</option>
                        <option value="note_updated">Updated note</option>
                        <option value="note_deleted">Deleted note</option>
                        <option value="expense_created">Added expense</option>
                        <option value="expense_updated">Updated expense</option>
                        <option value="expense_deleted">Deleted expense</option>
                      </select>
                    </div>
                    <button onClick={() => fetchActivityLogs(historyPage)} className="text-xs text-blue-600 hover:text-blue-800 flex items-center">
                      <span className="mr-1">🔄</span>
                      Refresh
                    </button>
                  </div>
                </div>
                {/* Simple list renderer */}
                <ActivityLogList activities={activityLogs} isLoading={isLoadingLogs} error={logsError} />
                {/* Pagination for Pro/Max */}
                {historyPlan && historyPlan !== 'basic' && historyTotal > pageSize && (
                  <div className="mt-3 flex items-center justify-between">
                    <button
                      className="px-3 py-1 text-sm rounded border disabled:opacity-50"
                      onClick={() => fetchActivityLogs(Math.max(0, historyPage - 1))}
                      disabled={historyPage === 0 || isLoadingLogs}
                    >
                      Previous
                    </button>
                    <div className="text-xs text-gray-600">
                      Page {historyPage + 1} of {Math.max(1, Math.ceil(historyTotal / pageSize))}
                    </div>
                    <button
                      className="px-3 py-1 text-sm rounded border disabled:opacity-50"
                      onClick={() => fetchActivityLogs(Math.min(historyPage + 1, Math.max(0, Math.ceil(historyTotal / pageSize) - 1)))}
                      disabled={historyPage >= Math.ceil(historyTotal / pageSize) - 1 || isLoadingLogs}
                    >
                      Next
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Reports */}
          {activeTab === 'reports' && (
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="p-4 border-b bg-gray-50">
                <h2 className="text-lg font-semibold text-gray-800 flex items-center">
                  <span className="mr-2">📊</span>
                  Reports
                </h2>
              </div>
              <div className="p-4 space-y-4">
                <div className="border rounded-lg p-4">
                  <div>
                    <div className="font-semibold text-gray-800">Expense Report</div>
                    <p className="text-sm text-gray-600 mt-1">Generate an expense summary for the selected month</p>
                    <div className="mt-2 flex items-center gap-2">
                      <label className="text-xs text-gray-600" htmlFor="report-month">Month:</label>
                      <MonthYearPicker
                        id="report-month"
                        value={reportMonth}
                        onChange={setReportMonth}
                        className="flex-1"
                      />
                    </div>
                    <div className="mt-3">
                      <button
                        onClick={handlePrintExpenseReport}
                        className="btn"
                        disabled={!planId || planId === 'basic'}
                        title="Printable version"
                      >
                        {planId && planId !== 'basic' ? 'Print' : 'Available in Pro plan'}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="border rounded-lg p-4">
                  <div>
                    <div className="font-semibold text-gray-800">Care Report</div>
                    <p className="text-sm text-gray-600 mt-1">Summary: how many days each dependent spent in Home A/B</p>
                    <div className="mt-2 flex items-center gap-2">
                      <label className="text-xs text-gray-600" htmlFor="care-report-month">Month:</label>
                      <MonthYearPicker
                        id="care-report-month"
                        value={careReportMonth}
                        onChange={setCareReportMonth}
                        className="flex-1"
                      />
                    </div>
                    <div className="mt-3">
                      <button
                        onClick={handlePrintCareReport}
                        className="btn"
                        disabled={!planId || planId === 'basic'}
                        title="Printable version"
                      >
                        {planId && planId !== 'basic' ? 'Print' : 'Available in Pro plan'}
                      </button>
                    </div>
                  </div>
                </div>
                <div className="border rounded-lg p-4">
                  <div>
                    <div className="font-semibold text-gray-800">Data Export (JSON)</div>
                    <p className="text-sm text-gray-600 mt-1">Download a copy of family data for the selected month</p>
                    <div className="mt-2 flex items-center gap-2">
                      <label className="text-xs text-gray-600" htmlFor="export-month">Month:</label>
                      <MonthYearPicker
                        id="export-month"
                        value={exportMonth}
                        onChange={setExportMonth}
                        className="flex-1"
                      />
                    </div>
                    <div className="mt-3">
                      <button
                        onClick={handleExportData}
                        disabled={isExporting || !family}
                        className="btn"
                      >
                        {isExporting ? 'Exporting…' : 'Download JSON'}
                      </button>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default Account;
